// src/components/map/Map.js - COMPLETE UPDATED VERSION
import React, { useEffect, useRef, useCallback, useMemo, useState, useImperativeHandle } from 'react';
import MapPlaceholder from '../MapPlaceholder';
import { MAPBOX_CONFIG } from './mapbox.config';
import './Map.css';

// 🔥 FIX: Global Mapbox GL instance with singleton pattern
let globalMapboxgl = null;
let mapboxLoadPromise = null;

const TRUCK_SVG = `
<svg viewBox="0 0 640 512" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
  <path fill="currentColor" d="M624 352h-16V243.9c0-12.7-5.1-24.9-14.1-33.9L494 110.1c-9-9-21.2-14.1-33.9-14.1H416V48c0-26.5-21.5-48-48-48H48C21.5 0 0 21.5 0 48v320c0 26.5 21.5 48 48 48h16c0 53 43 96 96 96s96-43 96-96h128c0 53 43 96 96 96s96-43 96-96h48c8.8 0 16-7.2 16-16v-32c0-8.8-7.2-16-16-16zM160 464c-26.5 0-48-21.5-48-48s21.5-48 48-48 48 21.5 48 48-21.5 48-48 48zm320 0c-26.5 0-48-21.5-48-48s21.5-48 48-48 48 21.5 48 48-21.5 48-48 48zm80-208H416V144h44.1l99.9 99.9V256z" />
</svg>`;

const loadMapboxGL = () => {
  if (globalMapboxgl) return Promise.resolve(globalMapboxgl);

  if (!mapboxLoadPromise) {
    mapboxLoadPromise = (async () => {
      try {
        if (typeof window !== 'undefined') {
          const mapboxgl = (await import('mapbox-gl')).default;

          // Configure Mapbox GL once
          mapboxgl.accessToken = MAPBOX_CONFIG.ACCESS_TOKEN || '';

          // Apply performance optimizations
          if (MAPBOX_CONFIG.BILLING_OPTIMIZATIONS?.maxParallelImageRequests) {
            mapboxgl.maxParallelImageRequests = MAPBOX_CONFIG.BILLING_OPTIMIZATIONS.maxParallelImageRequests;
          }

          globalMapboxgl = mapboxgl;
          return mapboxgl;
        }
      } catch (error) {
        console.error('[Map] Failed to load Mapbox GL:', error);
        throw error;
      }
    })();
  }

  return mapboxLoadPromise;
};

// Helper functions
const isValidCoordinate = (lat, lng) => {
  return (
    lat != null &&
    lng != null &&
    !isNaN(Number(lat)) &&
    !isNaN(Number(lng)) &&
    Math.abs(Number(lat)) <= 90 &&
    Math.abs(Number(lng)) <= 180
  );
};

const formatMissionTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return isNaN(d.getTime()) ? date : d.toLocaleString();
};

const createPopupContent = (chauffeur) => {
  const existing = document.getElementById('bfret-popup-style');
  if (existing) existing.remove();
  const style = document.createElement('style');
  style.id = 'bfret-popup-style';
  style.textContent = `
    .bfret-popup-wrap {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      width: 280px;
      max-width: 280px;
      background: #ffffff;
      color: #0f172a;
      -webkit-font-smoothing: antialiased;
      box-sizing: border-box;
    }
    .bfret-popup-wrap * { box-sizing: border-box; }

    /* ── Header ─────────────────────────────── */
    .bfret-popup-wrap .bp-header {
      padding: 16px 40px 14px 16px;
      display: flex;
      align-items: center;
      gap: 10px;
      border-bottom: 1px solid #f1f5f9;
    }
    .bfret-popup-wrap .bp-avatar {
      width: 40px; height: 40px;
      border-radius: 10px;
      background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 14px; color: #fff;
      flex-shrink: 0;
    }
    .bfret-popup-wrap .bp-header-info { flex: 1; min-width: 0; }
    .bfret-popup-wrap .bp-name {
      font-weight: 600; font-size: 14px; color: #0f172a;
      line-height: 1.2;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .bfret-popup-wrap .bp-vehicle {
      font-size: 11.5px; color: #64748b; margin-top: 3px;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      max-width: 100%;
    }

    /* ── Metrics (2 columns) ─────────────────── */
    .bfret-popup-wrap .bp-metrics {
      display: grid;
      grid-template-columns: 1fr 1fr;
      border-bottom: 1px solid #f1f5f9;
    }
    .bfret-popup-wrap .bp-metric {
      padding: 14px 12px;
      display: flex; flex-direction: column;
      min-width: 0;
    }
    .bfret-popup-wrap .bp-metric:first-child {
      border-right: 1px solid #f1f5f9;
    }
    .bfret-popup-wrap .bp-metric-label {
      font-size: 9.5px; font-weight: 600;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .bfret-popup-wrap .bp-metric-value {
      display: flex; align-items: baseline; gap: 3px;
      margin-top: 5px;
      min-width: 0;
    }
    .bfret-popup-wrap .bp-metric-num {
      font-size: 22px; font-weight: 700;
      line-height: 1;
      letter-spacing: -0.5px;
      color: #0f172a;
      white-space: nowrap;
    }
    .bfret-popup-wrap .bp-metric-num.muted { color: #cbd5e1; }
    .bfret-popup-wrap .bp-metric-unit {
      font-size: 11px; font-weight: 500; color: #94a3b8;
      white-space: nowrap;
    }

    /* ── Route ──────────────────────────────── */
    .bfret-popup-wrap .bp-route {
      padding: 12px 16px;
      border-bottom: 1px solid #f1f5f9;
    }
    .bfret-popup-wrap .bp-route-row {
      display: flex; align-items: flex-start; gap: 9px;
      min-width: 0;
    }
    .bfret-popup-wrap .bp-route-row + .bp-route-row {
      margin-top: 9px;
    }
    .bfret-popup-wrap .bp-route-dot {
      width: 8px; height: 8px; border-radius: 50%;
      flex-shrink: 0;
      margin-top: 4px;
    }
    .bfret-popup-wrap .bp-route-dot.from { background: #10b981; }
    .bfret-popup-wrap .bp-route-dot.to   { background: #ef4444; }
    .bfret-popup-wrap .bp-route-text {
      font-size: 12px; color: #334155;
      font-weight: 500; line-height: 1.35;
      flex: 1; min-width: 0;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      word-break: break-word;
    }

    /* ── Footer ─────────────────────────────── */
    .bfret-popup-wrap .bp-footer {
      padding: 10px 16px;
      display: flex; align-items: center; justify-content: space-between;
      gap: 8px;
      background: #fafbfc;
      border-radius: 0 0 12px 12px;
      min-width: 0;
    }
    .bfret-popup-wrap .bp-cargo {
      font-size: 11px; color: #64748b; font-weight: 500;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      flex: 1; min-width: 0;
      text-transform: capitalize;
    }
    .bfret-popup-wrap .bp-eta-arrival {
      font-size: 11px; font-weight: 600;
      color: #3b82f6;
      white-space: nowrap;
      flex-shrink: 0;
    }

    /* ── Mapbox popup chrome overrides ──────── */
    .bfret-custom-popup .mapboxgl-popup-content {
      padding: 0 !important;
      border-radius: 12px !important;
      overflow: hidden;
      box-shadow:
        0 10px 30px rgba(15, 23, 42, 0.14),
        0 0 0 1px rgba(15, 23, 42, 0.06) !important;
      background: #fff !important;
    }
    .bfret-custom-popup .mapboxgl-popup-tip {
      border-top-color: #fff !important;
      border-bottom-color: #fff !important;
    }
    .bfret-custom-popup .mapboxgl-popup-close-button {
      color: #94a3b8 !important;
      font-size: 20px !important;
      top: 8px !important;
      right: 8px !important;
      width: 24px; height: 24px;
      background: transparent !important;
      line-height: 1 !important;
      z-index: 10;
      border-radius: 6px;
      transition: background 0.15s, color 0.15s;
    }
    .bfret-custom-popup .mapboxgl-popup-close-button:hover {
      color: #0f172a !important;
      background: #f1f5f9 !important;
    }
  `;
  document.head.appendChild(style);

  // ── Vehicle info ──────────────────────────────
  let vehicleInfo = '';
  if (chauffeur.vehiculeBrand || chauffeur.vehiculePlate) {
    const brand = chauffeur.vehiculeBrand || '';
    const model = chauffeur.vehiculeModel || '';
    const plate = chauffeur.vehiculePlate ? `· ${chauffeur.vehiculePlate}` : '';
    vehicleInfo = `${brand} ${model} ${plate}`.trim();
  } else if (typeof chauffeur.vehicle === 'string') {
    vehicleInfo = chauffeur.vehicle;
  } else if (chauffeur.vehicle && typeof chauffeur.vehicle === 'object') {
    const { brand = '', marque = '', model = '', modele = '', plate = '', immatriculation = '' } = chauffeur.vehicle;
    vehicleInfo = `${brand || marque} ${model || modele} ${plate || immatriculation}`.trim();
  } else if (chauffeur.vehicule) {
    vehicleInfo = typeof chauffeur.vehicule === 'string'
      ? chauffeur.vehicule
      : `${chauffeur.vehicule.marque || ''} ${chauffeur.vehicule.modele || ''}`.trim();
  }
  if (!vehicleInfo) vehicleInfo = 'Véhicule non renseigné';

  // ── Name / initials ───────────────────────────
  const rawName = chauffeur.chauffeurName || chauffeur.name || 'Chauffeur';
  const name = rawName.replace(/^\[SUB\]\s*/, '');
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map(p => p[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  // ── Speed (m/s → km/h) ────────────────────────
  const rawSpeed = chauffeur.speed ?? chauffeur.vitesse ?? null;
  const speedKmh = rawSpeed != null ? Math.round(rawSpeed * 3.6) : null;
  const hasSpeed = speedKmh != null;

  // ── Time remaining ────────────────────────────
  const etaMin = chauffeur.remainingMinutes ?? null;
  const hasEta = etaMin != null && etaMin > 0;
  const etaHours = hasEta ? Math.floor(etaMin / 60) : 0;
  const etaMins  = hasEta ? etaMin % 60 : 0;
  const etaStr = hasEta
    ? (etaMin >= 60
        ? `${etaHours}h ${etaMins > 0 ? etaMins : ''}`.trim()
        : `${etaMin}`)
    : '—';
  const etaUnit = hasEta ? (etaMin >= 60 && etaMins === 0 ? '' : 'min') : '';

  // ── Distance ──────────────────────────────────
  const dist = chauffeur.distanceKm ?? null;
  const hasDist = dist != null;
  const distStr = hasDist ? dist.toFixed(1) : '—';

  // ── Estimated arrival clock ───────────────────
  const estimatedArrival =
    chauffeur.estimatedArrival ||
    chauffeur.estimatedTimeOfArrival ||
    null;

  // ── Mission type ──────────────────────────────
  const missionType =
    chauffeur.missionType ||
    chauffeur.typeMarchandise ||
    chauffeur.mission?.typeMarchandise ||
    null;

  const hasRoute = !!(chauffeur.departureLocation && chauffeur.arrivalLocation);

  return `
    <div class="bfret-popup-wrap">
      <div class="bp-header">
        <div class="bp-avatar">${initials || '?'}</div>
        <div class="bp-header-info">
          <div class="bp-name" title="${name}">${name}</div>
          <div class="bp-vehicle" title="${vehicleInfo}">${vehicleInfo}</div>
        </div>
      </div>

      <div class="bp-metrics">
        <div class="bp-metric">
          <div class="bp-metric-label">Temps restant</div>
          <div class="bp-metric-value">
            <span class="bp-metric-num ${hasEta ? '' : 'muted'}">${etaStr}</span>
            ${etaUnit ? `<span class="bp-metric-unit">${etaUnit}</span>` : ''}
          </div>
        </div>
        <div class="bp-metric">
          <div class="bp-metric-label">Distance</div>
          <div class="bp-metric-value">
            <span class="bp-metric-num ${hasDist ? '' : 'muted'}">${distStr}</span>
            ${hasDist ? `<span class="bp-metric-unit">km</span>` : ''}
          </div>
        </div>
        <div class="bp-metric">
          <div class="bp-metric-label">Vitesse</div>
          <div class="bp-metric-value">
            <span class="bp-metric-num ${hasSpeed ? '' : 'muted'}">${hasSpeed ? speedKmh : '—'}</span>
            ${hasSpeed ? `<span class="bp-metric-unit">km/h</span>` : ''}
          </div>
        </div>
      </div>

      ${hasRoute ? `
      <div class="bp-route">
        <div class="bp-route-row">
          <div class="bp-route-dot from"></div>
          <div class="bp-route-text">${chauffeur.departureLocation}</div>
        </div>
        <div class="bp-route-row">
          <div class="bp-route-dot to"></div>
          <div class="bp-route-text">${chauffeur.arrivalLocation}</div>
        </div>
      </div>` : ''}

      <div class="bp-footer">
        <div class="bp-cargo">
          ${missionType ? missionType.replace(/_/g, ' ').toLowerCase() : 'Pas de mission active'}
        </div>
        ${estimatedArrival ? `<div class="bp-eta-arrival">Arrivée · ${estimatedArrival}</div>` : ''}
      </div>
    </div>
  `;
};

const getOptimalZoom = (markerCount) => {
  if (markerCount <= 1) return 14;
  if (markerCount <= 3) return 13;
  if (markerCount <= 10) return 12;
  if (markerCount <= 20) return 11;
  if (markerCount <= 50) return 10;
  return 9;
};

// 🔥 FIX: Stable map instance hook
const useMapboxInstance = (containerRef, options = {}) => {
  const mapRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const initRef = useRef(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    const initializeMap = async () => {
      if (initRef.current || !containerRef.current || !isMounted.current) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const mapboxgl = await loadMapboxGL();

        if (!isMounted.current) return;

        if (!containerRef.current) {
          console.warn('[Map] Container disappeared during initialization');
          return;
        }

        const map = new mapboxgl.Map({
          container: containerRef.current,
          style: MAPBOX_CONFIG.MAP_STYLE || 'mapbox://styles/mapbox/streets-v11',
          center: options.center || [10.1815, 36.8065],
          zoom: options.zoom || 12,
          minZoom: options.minZoom || 2,
          maxZoom: options.maxZoom || 18,
          accessToken: MAPBOX_CONFIG.ACCESS_TOKEN,
          maxTileCacheSize: 100,
          failIfMajorPerformanceCaveat: true,
          preserveDrawingBuffer: false,
          antialias: false,
          attributionControl: false,
          renderWorldCopies: false,
          ...options
        });

        mapRef.current = map;
        initRef.current = true;

        map.addControl(new mapboxgl.NavigationControl({
          showCompass: false,
          visualizePitch: false,
        }), 'top-right');

        map.addControl(new mapboxgl.FullscreenControl(), 'top-right');

        map.on('load', () => {
          if (isMounted.current) {
            setIsLoading(false);
          }
        });

        map.on('error', (e) => {
          console.error('[Map] Map error:', e);
          if (isMounted.current && e.error) {
            setError(e.error.message || 'Map error occurred');
          }
        });

      } catch (err) {
        console.error('[Map] Failed to initialize map:', err);
        if (isMounted.current) {
          setError(err.message || 'Failed to load map');
          setIsLoading(false);
        }
        initRef.current = false;
      }
    };

    const timeoutId = setTimeout(() => {
      initializeMap();
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      isMounted.current = false;

      if (!isMounted.current && mapRef.current) {
        mapRef.current = null;
        initRef.current = false;
      }
    };
  }, []);

  return { mapRef, isLoading, error };
};

const MapComponent = React.forwardRef(({
  chauffeurs = [],
  mapRef: externalMapRef,
  containerRef: externalContainerRef,
  center,
  onMarkerClick,
  onMapLoad,
  className = '',
  style = {},
  enableClustering = false,
  enableAutoZoom = true,
  autoZoomDelay = 30000,
  zoom,
  maxZoom = 14,
  options: propOptions = {},
  ...props
}, ref) => {
  const internalContainerRef = useRef(null);
  const popupRef = useRef(null);
  const individualMarkersRef = useRef(new Map());
  const markerAnimationsRef = useRef(new Map());
  const markersInitialized = useRef(false);
  const renderCount = useRef(0);

  const containerRef = externalContainerRef || internalContainerRef;
  const handleMarkerClickRef = useRef(null);

  // 🔥 FIX: Optimized chauffeurs processing
  const { validChauffeurs, chauffeursKey } = useMemo(() => {
    if (!chauffeurs || !Array.isArray(chauffeurs)) {
      return { validChauffeurs: [], chauffeursKey: 'empty' };
    }

    const valid = [];
    let keyParts = [];

    const seenPositions = new Map();
    const JITTER_AMOUNT = 0.00003;

    chauffeurs.forEach((chauffeur, index) => {
      if (chauffeur && chauffeur.latitude != null && chauffeur.longitude != null) {
        let lat = parseFloat(chauffeur.latitude);
        let lng = parseFloat(chauffeur.longitude);

        if (isValidCoordinate(lat, lng)) {
          const isSub = !!(chauffeur.isSubcontracted || chauffeur.subcontracted);

          const posKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;
          if (seenPositions.has(posKey)) {
            const count = seenPositions.get(posKey);
            seenPositions.set(posKey, count + 1);
            const angle = count * 137.5;
            lat += Math.cos(angle * Math.PI / 180) * JITTER_AMOUNT * count;
            lng += Math.sin(angle * Math.PI / 180) * JITTER_AMOUNT * count;
          } else {
            seenPositions.set(posKey, 1);
          }

          const id = chauffeur.id || chauffeur.chauffeurId || `chauffeur-${index}`;
          valid.push({
            id,
            chauffeurId: chauffeur.chauffeurId || chauffeur.id?.toString() || id,
            chauffeurName: (isSub ? '[SUB] ' : '') + (chauffeur.chauffeurName || chauffeur.name || `Driver ${id}`),
            latitude: lat,
            longitude: lng,
            isSubcontracted: isSub,
            ...chauffeur,
          });
          keyParts.push(`${id}:${lat.toFixed(7)}:${lng.toFixed(7)}:${isSub ? 's' : 'i'}`);
        }
      }
    });

    const key = keyParts.join('|') || 'empty';

    return { validChauffeurs: valid, chauffeursKey: key };
  }, [chauffeurs]);

  const centerCoordinates = useMemo(() => {
    if (!center) return null;

    const lat = Number(center.latitude || center.lat);
    const lng = Number(center.longitude || center.lng);

    if (!isValidCoordinate(lat, lng)) return null;

    return [lng, lat];
  }, [center]);

  const mapOptions = useMemo(() => {
    const baseOptions = {
      style: MAPBOX_CONFIG.MAP_STYLE || 'mapbox://styles/mapbox/streets-v11',
      center: centerCoordinates || [10.1815, 36.8065],
      zoom: zoom !== undefined ? zoom : 6,
      minZoom: 2,
      maxZoom: 18,
      accessToken: MAPBOX_CONFIG.ACCESS_TOKEN,
      maxTileCacheSize: 100,
      failIfMajorPerformanceCaveat: true,
      preserveDrawingBuffer: false,
      antialias: false,
      attributionControl: false,
      renderWorldCopies: false,
      ...propOptions,
    };

    if (zoom === undefined && propOptions.zoom === undefined && validChauffeurs.length > 0) {
      baseOptions.zoom = Math.min(
        getOptimalZoom(validChauffeurs.length),
        maxZoom
      );
    }

    return baseOptions;
  }, [propOptions, centerCoordinates, validChauffeurs, maxZoom, zoom]);

  const { mapRef, isLoading: mapLoading, error: mapError } = useMapboxInstance(
    containerRef,
    mapOptions
  );

  useEffect(() => {
    if (externalMapRef && mapRef.current) {
      externalMapRef.current = mapRef.current;
    }
  }, [mapRef, externalMapRef]);

  const closePopup = useCallback(() => {
    if (popupRef.current) {
      try {
        popupRef.current.remove();
      } catch (e) {
        // Popup already removed
      }
      popupRef.current = null;
    }
  }, []);

  const handleMarkerClick = useCallback((chauffeur, position, event) => {
    if (!mapRef.current || !globalMapboxgl) return;

    closePopup();

    const popupEl = document.createElement('div');
    popupEl.className = 'map-popup-content';
    popupEl.innerHTML = createPopupContent(chauffeur);

    try {
      popupRef.current = new globalMapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        maxWidth: '300px',
        offset: 24,
        className: 'bfret-custom-popup',
        // No fixed anchor → Mapbox auto-flips to keep popup in view
      })
        .setLngLat(position)
        .setDOMContent(popupEl)
        .addTo(mapRef.current);
    } catch (error) {
      console.error('Failed to create popup:', error);
    }

    if (onMarkerClick) {
      onMarkerClick(chauffeur, event);
    }
  }, [mapRef, onMarkerClick, closePopup]);

  useEffect(() => {
    handleMarkerClickRef.current = handleMarkerClick;
  }, [handleMarkerClick]);

  const clearIndividualMarkers = useCallback(() => {
    individualMarkersRef.current.forEach((marker) => {
      marker.remove();
    });
    individualMarkersRef.current.clear();

    markerAnimationsRef.current.forEach(cancelAnimationFrame);
    markerAnimationsRef.current.clear();
  }, []);

  const createMarkerElement = useCallback((chauffeur) => {
    const el = document.createElement('div');
    el.className = 'mapbox-marker';
    el.style.display = 'flex';
    el.style.flexDirection = 'column';
    el.style.alignItems = 'center';

    const truckIcon = document.createElement('div');
    const isSub = !!(chauffeur.isSubcontracted || chauffeur.subcontracted);
    const color = isSub ? '#F59E0B' : '#3B82F6';

    truckIcon.style.cssText = `
      width: 40px;
      height: 40px;
      background-color: white;
      border-radius: 50%;
      border: 3px solid ${color};
      box-shadow: 0 2px 10px rgba(0,0,0,0.3);
      cursor: pointer;
      transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275), z-index 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${color};
    `;
    truckIcon.innerHTML = TRUCK_SVG;

    const heading = chauffeur.heading !== undefined ? chauffeur.heading : 0;
    const svg = truckIcon.querySelector('svg');
    if (svg) {
      svg.style.transform = `rotate(${heading}deg)`;
      svg.style.transition = 'transform 0.5s ease-out';
    }

    const label = document.createElement('div');
    label.style.cssText = `
      background: rgba(255, 255, 255, 0.9);
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 11px;
      font-weight: 600;
      color: #1e3a8a;
      box-shadow: 0 1px 4px rgba(0,0,0,0.1);
      margin-top: 4px;
      white-space: nowrap;
      pointer-events: none;
    `;
    label.innerText = chauffeur.chauffeurName || 'Driver';

    el.addEventListener('mouseenter', () => {
      truckIcon.style.transform = 'scale(1.2)';
      el.style.zIndex = '1000';
    });

    el.addEventListener('mouseleave', () => {
      truckIcon.style.transform = 'scale(1)';
      el.style.zIndex = '';
    });

    el.appendChild(truckIcon);
    el.appendChild(label);

    return el;
  }, []);

  const animateMarkerTo = useCallback((markerId, marker, startLng, startLat, endLng, endLat) => {
    if (markerAnimationsRef.current.has(markerId)) {
      cancelAnimationFrame(markerAnimationsRef.current.get(markerId));
    }

    const startTime = performance.now();
    const duration = 1500;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const ease = 1 - Math.pow(1 - progress, 3);

      const currentLng = startLng + (endLng - startLng) * ease;
      const currentLat = startLat + (endLat - startLat) * ease;

      marker.setLngLat([currentLng, currentLat]);

      if (progress < 1) {
        markerAnimationsRef.current.set(markerId, requestAnimationFrame(animate));
      } else {
        markerAnimationsRef.current.delete(markerId);
      }
    };

    markerAnimationsRef.current.set(markerId, requestAnimationFrame(animate));
  }, []);

  const updateMarkers = useCallback(() => {
    if (!mapRef.current || !globalMapboxgl) return;

    const checkedIds = new Set();

    validChauffeurs.forEach(chauffeur => {
      const id = chauffeur.id || chauffeur.chauffeurId;
      if (!id) return;

      checkedIds.add(id);
      const targetLng = Number(chauffeur.longitude);
      const targetLat = Number(chauffeur.latitude);

      if (individualMarkersRef.current.has(id)) {
        const marker = individualMarkersRef.current.get(id);
        const currentPos = marker.getLngLat();

        if (Math.abs(currentPos.lng - targetLng) > 0.000001 || Math.abs(currentPos.lat - targetLat) > 0.000001) {
          animateMarkerTo(id, marker, currentPos.lng, currentPos.lat, targetLng, targetLat);

          const heading = chauffeur.heading !== undefined ? chauffeur.heading : 0;
          const el = marker.getElement();
          const svg = el.querySelector('svg');
          if (svg) {
            svg.style.transform = `rotate(${heading}deg)`;
          }
        }
      } else {
        try {
          const el = createMarkerElement(chauffeur);
          const marker = new globalMapboxgl.Marker({
            element: el,
            anchor: 'center'
          })
            .setLngLat([targetLng, targetLat])
            .addTo(mapRef.current);

          el.addEventListener('mouseenter', (e) => {
            e.stopPropagation();
            handleMarkerClickRef.current?.(chauffeur, [targetLng, targetLat], { originalEvent: e });
          });

          el.addEventListener('click', (e) => {
            e.stopPropagation();
            handleMarkerClickRef.current?.(chauffeur, [targetLng, targetLat], { originalEvent: e });
          });

          individualMarkersRef.current.set(id, marker);
        } catch (error) {
          console.error(`[Map] Error creating marker for ${id}:`, error);
        }
      }
    });

    individualMarkersRef.current.forEach((marker, id) => {
      if (!checkedIds.has(id)) {
        marker.remove();
        individualMarkersRef.current.delete(id);
        if (markerAnimationsRef.current.has(id)) {
          cancelAnimationFrame(markerAnimationsRef.current.get(id));
          markerAnimationsRef.current.delete(id);
        }
      }
    });

    return true;
  }, [mapRef, validChauffeurs, createMarkerElement, handleMarkerClick, animateMarkerTo]);

  useEffect(() => {
    const currentRender = ++renderCount.current;

    if (!mapRef.current || !globalMapboxgl) {
      return;
    }

    markersInitialized.current = true;

    const timer = setTimeout(() => {
      if (currentRender !== renderCount.current) return;

      updateMarkers();

    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [chauffeursKey, mapRef.current, updateMarkers, enableAutoZoom, maxZoom, validChauffeurs]);

  useEffect(() => {
    if (!mapRef.current || !globalMapboxgl) return;

    const savedView = sessionStorage.getItem('map_view_state');
    let hasRestored = false;

    if (savedView) {
      try {
        const { center, zoom } = JSON.parse(savedView);
        if (center && zoom) {
          mapRef.current.jumpTo({
            center,
            zoom: Math.min(zoom, 10),
          });
          hasRestored = true;
        }
      } catch (e) {
        console.warn('Failed to parse saved map state');
      }
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (!mapRef.current) return;
          const { latitude, longitude } = position.coords;

          if (!hasRestored) {
            mapRef.current.flyTo({
              center: [longitude, latitude],
              zoom: 6,
              essential: true,
              duration: 2500
            });
          }
        },
        (error) => {
          console.warn('[Map] Geolocation failed:', error);
        },
        { timeout: 5000, enableHighAccuracy: false }
      );
    }

    const handleResize = () => {
      if (mapRef.current) {
        mapRef.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    const saveState = () => {
      if (!mapRef.current) return;
      const center = mapRef.current.getCenter();
      const zoom = mapRef.current.getZoom();
      sessionStorage.setItem('map_view_state', JSON.stringify({ center, zoom }));
    };

    mapRef.current.on('moveend', saveState);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (mapRef.current) {
        mapRef.current.off('moveend', saveState);
      }
    };

  }, [mapRef.current]);

  useEffect(() => {
    return () => {
      closePopup();
      clearIndividualMarkers();
      markersInitialized.current = false;
      renderCount.current = 0;
    };
  }, [closePopup, clearIndividualMarkers]);

  useImperativeHandle(ref, () => ({
    getMap: () => mapRef.current,
    fitBounds: (bounds, options) => {
      if (mapRef.current && globalMapboxgl) {
        try {
          return mapRef.current.fitBounds(bounds, {
            padding: 50,
            maxZoom: maxZoom,
            duration: 1000,
            ...options
          });
        } catch (error) {
          console.error('[Map] fitBounds failed:', error);
        }
      }
    },
    flyTo: (options) => {
      if (mapRef.current) {
        try {
          return mapRef.current.flyTo({
            duration: 2000,
            essential: true,
            ...options
          });
        } catch (error) {
          console.error('[Map] flyTo failed:', error);
        }
      }
    },
    getCenter: () => mapRef.current?.getCenter?.(),
    getZoom: () => mapRef.current?.getZoom?.(),
    closePopup,
    getStatus: () => ({
      mapReady: !!mapRef.current,
      chauffeurs: validChauffeurs.length,
      markersRendered: individualMarkersRef.current.size,
      markersInitialized: markersInitialized.current,
      renderCount: renderCount.current,
    }),
  }), [mapRef, maxZoom, closePopup, validChauffeurs]);

  const containerStyle = useMemo(() => ({
    width: '100%',
    height: '100%',
    minHeight: '500px',
    position: 'relative',
    overflow: 'hidden',
    ...style,
  }), [style]);

  if (mapError) {
    return (
      <div style={containerStyle} className={`map-container ${className}`}>
        <div className="map-error">
          <p>Failed to load map: {mapError}</p>
          {mapError.includes('token') && (
            <p className="small">
              Please check your .env file and ensure REACT_APP_MAPBOX_TOKEN is set.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={containerStyle}
      className={`map-container ${className}`}
      role="application"
      aria-label="Interactive map"
    >
      {mapLoading && <MapPlaceholder />}
    </div>
  );
});

// 🔥 FIX: Simple memoization for Map component
const arePropsEqual = (prevProps, nextProps) => {
  if (prevProps.chauffeurs !== nextProps.chauffeurs) {
    const prev = prevProps.chauffeurs || [];
    const next = nextProps.chauffeurs || [];

    if (prev.length !== next.length) return false;

    for (let i = 0; i < prev.length; i++) {
      const p = prev[i];
      const n = next[i];

      if (!p || !n) return false;
      if (p.id !== n.id) return false;
      if (p.latitude !== n.latitude || p.longitude !== n.longitude) return false;
    }
  }

  const propsToCompare = [
    'center', 'zoom', 'enableAutoZoom', 'enableClustering',
    'onMarkerClick', 'style', 'options', 'className'
  ];

  for (const prop of propsToCompare) {
    if (prevProps[prop] !== nextProps[prop]) {
      return false;
    }
  }

  return true;
};

export default React.memo(MapComponent, arePropsEqual);